// Reshapes the Receivables aging-bucket response into one row per student, worst-overdue
// first — the Collect tab's "who owes money, and how much" queue. Pure function, no fetch:
// it takes the exact same { summary: { buckets } } shape GET /api/finance/receivables already
// returns (and Receivables.jsx already renders), so Collect never recomputes a balance or an
// aging bucket — it only groups rows that are already correct.

/**
 * @param {Object} receivablesData - the raw response from GET /api/finance/receivables,
 *   i.e. { summary: { total, studentCount, buckets: { current, days_1_30, days_31_60, days_60_plus } } }
 * @returns {{ totalOutstanding: number, studentCount: number, students: Array }}
 *   students: [{ studentId, studentName, studentPhotoUrl, studentTxId, studentPhone, totalOwed,
 *                daysOverdue, unpaidMonths: [{ feePaymentId, feeMonthLabel, amount, type, description }] }]
 *   sorted by daysOverdue descending (worst first).
 */
export function buildCollectQueue(receivablesData) {
  const buckets = receivablesData?.summary?.buckets || {};
  const allRows = Object.values(buckets).flatMap(b => b?.students || []);

  const byStudent = new Map();
  for (const row of allRows) {
    const key = row.studentId ? String(row.studentId) : `unknown-${row.feePaymentId}`;
    const existing = byStudent.get(key);
    const entry = {
      feePaymentId: row.feePaymentId,
      feeMonthLabel: row.feeMonthLabel,
      amount: row.amount,
      type: row.type,
      description: row.description,
      isProrated: row.isProrated || false,
      billableDays: row.billableDays || null,
    };
    if (existing) {
      existing.totalOwed += row.amount;
      existing.daysOverdue = Math.max(existing.daysOverdue, row.daysOverdue);
      existing.unpaidMonths.push(entry);
    } else {
      byStudent.set(key, {
        studentId:       row.studentId,
        studentName:     row.studentName,
        studentPhotoUrl: row.studentPhotoUrl,
        studentTxId:     row.studentTxId,
        studentPhone:    row.studentPhone,
        totalOwed:       row.amount,
        daysOverdue:     row.daysOverdue,
        unpaidMonths:    [entry],
      });
    }
  }

  const students = [...byStudent.values()].sort((a, b) => b.daysOverdue - a.daysOverdue);
  const totalOutstanding = students.reduce((sum, s) => sum + s.totalOwed, 0);

  return { totalOutstanding, studentCount: students.length, students };
}

// Filters a queue's students by overdue tier + name search. Pure and exported so it's testable
// on its own — this is exactly the logic that used to live inline in CollectTab's `filtered`
// computation, where an "All" tier with a `min: 0` floor silently excluded every student whose
// worst daysOverdue was negative (a fee generated but not yet due) — even on the tab labeled
// "All." `minDays` defaults to `-Infinity` so "no floor" has to be opted OUT of, not into.
export function filterQueueStudents(students, { minDays = -Infinity, search = '' } = {}) {
  const term = search.trim().toLowerCase();
  return students.filter(s =>
    s.daysOverdue >= minDays &&
    (!term || s.studentName.toLowerCase().includes(term))
  );
}

// Polite, factual wa.me reminder text for one grouped student (as produced by
// buildCollectQueue above) — a human reviews and sends, so no promises/threats, just the
// facts: who, how much, which months. Suited to a parent of a child at the club. Shared by
// the Collect tab and Receivables so both quote the same complete picture (every unpaid
// month, not just whichever single row was clicked).
export function buildCollectWhatsAppMessage(student, clubName) {
  const monthsList = student.unpaidMonths
    .map(m => m.type === 'custom' ? (m.description || 'Custom charge') : m.feeMonthLabel)
    .join(', ');
  return (
    `Namaste, this is a reminder from ${clubName || 'our club'} regarding ${student.studentName}'s fees.\n\n` +
    `Outstanding amount: NPR ${student.totalOwed.toLocaleString()}\n` +
    `For: ${monthsList}\n\n` +
    `Please let us know if you have any questions. Thank you.`
  );
}
